import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useCartStore } from '@/lib/store';

// Main navigation - for the scrolling banner
const mainNavigation = [
  { name: 'La Villa', href: '/la-villa' },
  { name: 'Estate 2027', href: '/estate-2027' },
  { name: 'Abbonamenti', href: '/abbonamenti' },
  { name: 'Night', href: '/night-events' },
  { name: 'Shop', href: '/shop' },
  { name: 'Contatti', href: '/contatti' },
  { name: 'Food & Drink', href: '/food-drink' },
  { name: 'Eventi Privati', href: '/eventi-privati' },
  { name: 'Tour 3D', href: '/virtual-tour' },
];

// All navigation for desktop
const allNavigation = [
  { name: 'La Villa', href: '/la-villa' },
  { name: 'Estate 2027', href: '/estate-2027' },
  { name: 'Abbonamenti', href: '/abbonamenti' },
  { name: 'Food & Drink', href: '/food-drink' },
  { name: 'Eventi Privati', href: '/eventi-privati' },
  { name: 'Night', href: '/night-events' },
  { name: 'Shop', href: '/shop' },
  { name: 'Tour 3D', href: '/virtual-tour' },
  { name: 'Contatti', href: '/contatti' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const marqueeRef = useRef(null);
  const location = useLocation();
  const itemCount = useCartStore((state) => state.getItemCount());
  
  const isNightPage = location.pathname === '/night-events';
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Handle touch events to pause animation and allow manual scroll
  const handleTouchStart = () => {
    setIsTouching(true);
  };
  
  const handleTouchEnd = () => {
    // Resume animation after a short delay
    setTimeout(() => setIsTouching(false), 2000);
  };
  
  // Double the items for seamless loop
  const scrollingItems = [...mainNavigation, ...mainNavigation, ...mainNavigation];
  
  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'glass shadow-sm py-2 lg:py-3'
          : isNightPage
          ? 'bg-transparent py-3 lg:py-6'
          : 'bg-transparent py-3 lg:py-6'
      }`}
      data-testid="header"
    >
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 md:px-12 lg:px-24">
        
        {/* Mobile Layout - Single Row with Scrolling Banner */}
        <div className="lg:hidden flex items-center gap-2">
          {/* Logo */}
          <Link
            to="/"
            className="flex-shrink-0 transition-opacity hover:opacity-80"
            data-testid="logo-link"
          >
            <img 
              src={scrolled || !isNightPage ? "/images/logo-new.png" : "/images/logo-new-white.png"} 
              alt="La Villa" 
              className="h-7 sm:h-8"
            />
          </Link>
          
          {/* Scrolling Navigation Banner */}
          <div 
            ref={marqueeRef}
            className={`flex-1 overflow-hidden mx-2 marquee-container ${isTouching ? 'is-touching' : ''}`}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleTouchStart}
            onMouseUp={handleTouchEnd}
            onMouseLeave={handleTouchEnd}
          >
            <div className="marquee-content">
              {scrollingItems.map((item, index) => (
                <Link
                  key={`${item.name}-${index}`}
                  to={item.href}
                  className={`font-body text-[9px] uppercase tracking-wider whitespace-nowrap px-2 py-0.5 transition-colors ${
                    location.pathname === item.href
                      ? 'text-champagne font-medium'
                      : scrolled || !isNightPage
                      ? 'text-night/70 hover:text-champagne'
                      : 'text-ivory/70 hover:text-champagne'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
          
          {/* Right Side Actions */}
          <div className="flex-shrink-0 flex items-center gap-1">
            {/* Cart */}
            <Link
              to="/shop"
              className={`relative p-1 transition-colors ${
                scrolled || !isNightPage ? 'text-night hover:text-champagne' : 'text-ivory hover:text-champagne'
              }`}
              data-testid="cart-link-mobile"
            >
              <ShoppingBag size={18} strokeWidth={1.5} />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-champagne text-white text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            
            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-7 w-7 ${scrolled || !isNightPage ? 'text-night' : 'text-ivory'}`}
                  data-testid="mobile-menu-button"
                >
                  <Menu size={18} strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-ivory border-none w-full sm:w-[400px]">
                <div className="flex flex-col h-full pt-12">
                  <p className="font-body text-xs uppercase tracking-[0.2em] text-champagne mb-6">
                    Menu
                  </p>
                  <nav className="flex flex-col gap-6">
                    {mainNavigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileOpen(false)}
                        className={`font-heading text-3xl tracking-tight transition-colors hover:text-champagne ${
                          location.pathname === item.href ? 'text-champagne' : 'text-night'
                        }`}
                        data-testid={`mobile-nav-${item.name.toLowerCase().replace(' ', '-')}`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </nav>
                  
                  <div className="mt-auto pb-8">
                    <p className="font-accent text-lg italic text-stone-500">
                      "Ogni gesto, una firma."
                    </p>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        
        {/* Desktop Layout - Single Row */}
        <div className="hidden lg:flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="transition-opacity hover:opacity-80"
            data-testid="logo-link-desktop"
          >
            <img 
              src={scrolled || !isNightPage ? "/images/logo-new.png" : "/images/logo-new-white.png"} 
              alt="La Villa" 
              className="h-12"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="flex items-center gap-8" data-testid="desktop-nav">
            {allNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`font-body text-sm uppercase tracking-widest transition-colors hover:text-champagne ${
                  location.pathname === item.href
                    ? 'text-champagne'
                    : scrolled || !isNightPage
                    ? 'text-night'
                    : 'text-ivory'
                }`}
                data-testid={`nav-link-${item.name.toLowerCase().replace(' ', '-')}`}
              >
                {item.name}
              </Link>
            ))}
          </nav>
          
          {/* Cart */}
          <Link
            to="/shop"
            className={`relative p-2 transition-colors ${
              scrolled || !isNightPage ? 'text-night hover:text-champagne' : 'text-ivory hover:text-champagne'
            }`}
            data-testid="cart-link"
          >
            <ShoppingBag size={22} strokeWidth={1.5} />
            {itemCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-champagne text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {itemCount}
              </span>
            )}
          </Link>
        </div>
        
      </div>
    </header>
  );
}
